import React, { useEffect, useState } from "react";
import { getSearchCountList } from "../../Service/apiService";
import "../../assets/css/DataTable.css";

export default function UserSearchCountList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filteredRows, setFilteredRows] = useState([]);

  const [searchQuery, setSearchQuery] = useState("");

  // Filter state
  const [filterOption, setFilterOption] = useState("");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const getResponsiveRows = () => {
  if (window.innerHeight > 900) return 20;   // big screens
  if (window.innerHeight > 750) return 14;   // laptop
  return 8;                                  // small screens / short viewport
};
  const [rowsPerPage, setRowsPerPage] = useState(getResponsiveRows());

  useEffect(() => {
  const handleResize = () => setRowsPerPage(getResponsiveRows());
  window.addEventListener("resize", handleResize);

  return () => window.removeEventListener("resize", handleResize);
}, []);

  // Strict DD/MM/YYYY parser with validation
  const parseDate = (str) => {
    if (!str) return new Date(NaN);

    const clean = str.trim().replace(/-/g, "/");
    const parts = clean.split("/").map(Number);

    if (parts.length !== 3) return new Date(NaN);

    const [d, m, y] = parts;

    // Validate ranges
    if (
      y < 1000 ||
      y > 9999 ||
      m < 1 ||
      m > 12 ||
      d < 1 ||
      d > 31
    ) {
      return new Date(NaN);
    }

    const date = new Date(y, m - 1, d);

    // Verify no rollover (e.g., 32/1/2025 → Feb 1)
    if (
      date.getFullYear() !== y ||
      date.getMonth() !== m - 1 ||
      date.getDate() !== d
    ) {
      return new Date(NaN);
    }

    return date;
  };

  useEffect(() => {
    fetchSearchData();
  }, []);

  const fetchSearchData = async () => {
    try {
      const data = await getSearchCountList();
    

      const formattedData = data.result
        .map((item, index) => {
          const dateObj = parseDate(item.date);
          if (isNaN(dateObj.getTime())) {
            console.warn("⚠️ Invalid date format in API response:", item.date);
            return null;
          }
          return {
            index: index + 1,
            date: item.date,
            curatedCount: item.curatedCount,
            mainCount: item.mainCount,
            globalCount: item.globalCount,
            dateObj,
          };
        })
        .filter(Boolean) // Remove invalid entries
        .sort((a, b) => b.dateObj - a.dateObj); // Sort descending

      setRows(formattedData);
      setFilteredRows(formattedData);
    } catch (error) {
      console.error("Error fetching search count list:", error);
    } finally {
      setLoading(false);
    }
  };

  // Filter effect
  useEffect(() => {
    if (!filterOption) {
      setFilteredRows(rows);
      setCurrentPage(1);
      return;
    }

    let startDate, endDate;

    if (filterOption === "custom") {
      if (!customStart || !customEnd) {
        setFilteredRows(rows);
        setCurrentPage(1);
        return;
      }

      // <input type="date"> gives YYYY-MM-DD → parse as LOCAL date
      const parseLocalDate = (str) => {
        const [y, m, d] = str.split("-").map(Number);
        return new Date(y, m - 1, d);
      };

      startDate = parseLocalDate(customStart);
      endDate = parseLocalDate(customEnd);
      // Set end date to end of day (local)
      endDate.setHours(23, 59, 59, 999);
    } else {
      const now = new Date();
      // End of today (local)
      endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      endDate.setHours(23, 59, 59, 999);

      if (filterOption === "1w") {
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
      } else if (filterOption === "1m") {
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
      } else if (filterOption === "3m") {
        startDate = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
      } else if (filterOption === "6m") {
        startDate = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
      } else if (filterOption === "1y") {
        startDate = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
      }
    }

    // Validate filter dates
    if (isNaN(startDate?.getTime()) || isNaN(endDate?.getTime())) {
      console.warn("Invalid filter date range");
      setFilteredRows(rows);
      setCurrentPage(1);
      return;
    }

    const filtered = rows.filter((item) => {
      const itemDate = new Date(
        item.dateObj.getFullYear(),
        item.dateObj.getMonth(),
        item.dateObj.getDate()
      );
      return itemDate >= startDate && itemDate <= endDate;
    });

    // console.log("FilterWhere:", { startDate, endDate, totalFiltered: filtered.length });

    setFilteredRows(filtered);
    setCurrentPage(1);
  }, [filterOption, customStart, customEnd, rows]);

  if (loading) return <p style={{ padding: "20px" }}>Loading...</p>;

  // Pagination
  const totalPages = Math.ceil(filteredRows.length / rowsPerPage);
  const lastRowIndex = currentPage * rowsPerPage;
  const firstRowIndex = lastRowIndex - rowsPerPage;
  const currentRows = filteredRows.slice(firstRowIndex, lastRowIndex);

  return (
    <div className="data-table-wrapper">
    <div className="data-table-header">
  <h2>User Search Count</h2>
  <div className="search-filter-controls">
    <select
      className="search-input"
      value={filterOption}
      onChange={(e) => setFilterOption(e.target.value)}
    >
      <option value="">Filter By</option>
      <option value="1w">Last 1 Week</option>
      <option value="1m">Last 1 Month</option>
      <option value="3m">Last 3 Months</option>
      <option value="6m">Last 6 Months</option>
      <option value="1y">Last 1 Year</option>
      <option value="custom">Custom Range</option>
    </select>

    {filterOption === "custom" && (
      <div className="custom-date-wrapper">
        <input
          type="date"
          value={customStart}
          onChange={(e) => setCustomStart(e.target.value)}
          aria-label="Start date"
        />
        <input
          type="date"
          value={customEnd}
          onChange={(e) => setCustomEnd(e.target.value)}
          aria-label="End date"
        />
      </div>
    )}
  </div>
</div>

      <div className="table-wrapper">
        <table className="custom-table">
          <thead>
            <tr>
              <th>No.</th>
              <th>Date</th>
              <th>Curated Count</th>
              <th>Main Count</th>
              <th>Global Count</th>
            </tr>
          </thead>
          <tbody>
            {currentRows.length > 0 ? (
              currentRows.map((r) => (
                <tr key={r.date}> {/* Use unique date or _id if available */}
                  <td>{r.index}</td>
                  <td>{r.date}</td>
                  <td>{r.curatedCount?.toLocaleString() ?? "-"}</td>
                  <td>{r.mainCount?.toLocaleString() ?? "-"}</td>
                  <td>{r.globalCount?.toLocaleString() ?? "-"}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" style={{ textAlign: "center" }}>
                  No data found for the selected date range.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="pagination">
        <button
          disabled={currentPage === 1}
          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
        >
          ⬅ Prev
        </button>

        <span>Page {currentPage} of {totalPages || 1}</span>

        <button
          disabled={currentPage >= totalPages}
          onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
        >
          Next ➡
        </button>
      </div>
    </div>
  );
}